import { CloseOutlined } from "@ant-design/icons";
import {
  Button,
  Divider,
  Flex,
  Form,
  FormProps,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Typography
} from "antd";
import { LandingPage, MobileApp, TEditUser, TUser } from "api/services/models/types";
import { addOrUpdateUser, getUser } from "api/services/UserService";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { AxiosError } from "axios";
import ErrorViewer from "components/ErrorViewer";
import { usePicklists } from "components/picklists/PicklistContextProvider";
import { ReactComponent as TickSquareLinear } from "Icons/vuesax/tick-square.svg";
import { BaseSelectRef } from "rc-select";
import { useEffect, useRef, useState } from "react";

type TAddOrUpdateUserProps = {
  user?: TUser;
  onClose: (user?: TUser) => void;
};

type UserFields = {
  userId: string;
  contactId: number;
  providerIds: number[];
  locationIds: number[];
  roleIds: number[];
  landingPage: string;
  mobileApp: string;
};

const Header = ({
  user,
  onSubmit,
  onClose
}: {
  user?: TUser;
  onSubmit: () => void;
  onClose: () => void;
}) => (
  <Flex wrap gap="middle" align="start" justify="space-between" style={{ marginBottom: "16px" }}>
    <Typography.Title style={{ fontSize: "1rem" }} className="font-1212">{!user ? "Add User" : "Edit User"}</Typography.Title>
    <Space size="small">
      <Button
        type="primary"
        htmlType="submit"
        className="default-button primary-btn"
        onClick={onSubmit}>
        <TickSquareLinear className="default-icon" />
        <span>Save</span>
      </Button>
      <Button
        style={{ border: "none" }}
        icon={<CloseOutlined style={{ fontSize: ".75rem" }} />}
        onClick={e => onClose()}></Button>
    </Space>
  </Flex>
);

const AddOrUpdateUser = ({ user, onClose }: TAddOrUpdateUserProps) => {
  const { accountId } = useTDAuthentication();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AxiosError | null>();
  const { locations, contacts, providers, roles } = usePicklists();
  const [localUser, setLocalUser] = useState<TEditUser>();
  const [form] = Form.useForm();
  const locationRef = useRef<BaseSelectRef | null>(null);
  const providerRef = useRef<BaseSelectRef | null>(null);

  const selectedContactId = Form.useWatch('contactId', form);

  const isSelectedContactProvider = () => {
    if (!selectedContactId) return false;

    const selectedContact = contacts?.find(c => c.contactId === selectedContactId);
    if (!selectedContact) return false;

    return providers?.some(
      provider => provider.providerName.trim().toLowerCase() === selectedContact.contactName.trim().toLowerCase()
    );
  };

  useEffect(() => {
    if (!selectedContactId || !contacts || !providers) return;

    const selectedContact = contacts.find(c => c.contactId === selectedContactId);
    if (!selectedContact) return;

    const matchingProvider = providers.find(
      provider => provider.providerName.trim().toLowerCase() === selectedContact.contactName.trim().toLowerCase()
    );

    if (matchingProvider) {
      form.setFieldsValue({
        providerIds: [matchingProvider.providerId]
      });
    }
  }, [selectedContactId, contacts, providers, form]);

  useEffect(() => {
    if (accountId && user != null) {
      setLoading(true);
      setError(null);
      getUser(accountId, user.userId)
        .then(setLocalUser)
        .catch(setError)
        .finally(() => setLoading(false));
    }
  }, []);

  useEffect(() => {
    if (localUser) {
      form.setFieldsValue({
        userId: localUser?.userId,
        contactId: localUser?.contactId,
        providerIds:
          (localUser?.authorizedProviders?.some(p => p === 0)
            ? providers?.map(p => p.providerId)
            : localUser?.authorizedProviders) ?? [],
        roleIds: localUser?.authorizedRoles,
        locationIds: localUser?.authorizedLocations.some(l => l === 0)
          ? locations?.map(l => l.locationId)
          : localUser?.authorizedLocations,
        landingPage: localUser?.landingPage.split(".")[0] ?? "Inbox",
        mobileApp: localUser?.mobileApp ?? "VScribe"
      });
    }
  }, [localUser]);

  const isNewUser = user == null;

  const handleLocationSelectAll = () => {
    form.setFieldsValue({ locationIds: locations?.map(l => l.locationId) });
    form.validateFields(["locationIds"]);
    locationRef?.current?.focus();
    locationRef?.current?.blur();
  };

  const handleProvidersSelectAll = () => {
    form.setFieldsValue({ providerIds: providers?.map(l => l.providerId) });
    form.validateFields(["providerIds"]);
    providerRef?.current?.focus();
    providerRef?.current?.blur();
  };

  const locationRender = (menu: React.ReactNode) => (
    <>
      {menu}
      <Divider style={{ margin: "8px" }} />
      <div style={{ backgroundColor: "lightgray", padding: "5px" }}>
        <Button
          shape="round"
          onClick={handleLocationSelectAll}
          disabled={locations?.length === 0}>
          Select All
        </Button>
      </div>
    </>
  );

  const providerRenderer = (menu: React.ReactNode) => (
    <>
      {menu}
      <Divider style={{ margin: "8px" }} />
      <div style={{ backgroundColor: "lightgray", padding: "5px" }}>
        <Button
          shape="round"
          disabled={providers?.length === 0}
          onClick={handleProvidersSelectAll}>
          Select All
        </Button>
      </div>
    </>
  );

  const onUserIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
    form.setFieldsValue({ userId: inputValue });
  };

  const onSave = () => {
    form
      .validateFields()
      .then(() => form.submit())
      .catch(e => console.log(e));
  };
  const onFinish: FormProps<UserFields>["onFinish"] = values => {
    const payload = {
      userId: values.userId,
      accountId: accountId!,
      contactId: values.contactId,
      authorizedLocations:
        (locations?.length ?? 0) > 0 &&
        locations?.every(l => values.locationIds.some(i => i === l.locationId))
          ? [0]
          : values.locationIds,
      authorizedProviders:
        (providers?.length ?? 0) > 0 &&
        providers?.every(l => values.providerIds?.some(i => i === l.providerId))
          ? [0]
          : values.providerIds,
      authorizedRoles: values.roleIds,
      landingPage: values.landingPage,
      mobileApp: values.mobileApp ?? "VScribe"
    };
    setLoading(true);
    setError(null);
    addOrUpdateUser(payload, user == null)
      .then(onClose)
      .catch(setError)
      .finally(() => setLoading(false));
  };

  return (
    <Modal
      width={800}
      maskClosable={false}
      title={<Header user={user} onSubmit={onSave} onClose={onClose} />}
      open={true}
      footer={null}
      closeIcon={null}
      centered={true}
      onCancel={() => onClose()}>
      {loading && (
        <Flex justify="space-around" style={{ width: "100%", textAlign: "center" }}>
          <Spin tip="Saving, please wait..." />
        </Flex>
      )}
      {error && <ErrorViewer error={error} />}
      <Form
        form={form}
        name="add_or_update_user"
        initialValues={localUser}
        onFinish={onFinish}
        style={{ maxWidth: 800}}
        layout={"vertical"}
        >
        <Form.Item
          key={"userId"}
          label={<label htmlFor="userId" className="input-label">
            User Id
            </label>}
          name={"userId"}
          rules={[
            {
              required: true,
              message: "User Id is required!"
            }
          ]}>
          <Input
            readOnly={!isNewUser}
            autoComplete="false"
            maxLength={20}
            onChange={onUserIdChange}
            style={{ textTransform: "uppercase" }}
            className="filter-input-transparent"
          />
        </Form.Item>
        <Form.Item
          key={"contact"}
          name={`contactId`}
          label={<label htmlFor="contactId" className="input-label">
            Contact
            </label>}
          rules={[
            {
              required: true,
              message: "Contact is required!"
            }
          ]}>
          <Select
            fieldNames={{ label: "contactName", value: "contactId" }}
            options={contacts}
            variant="borderless"
            showSearch={false} className="filter-input-transparent"
          />
        </Form.Item>

        <Form.Item
          key={"location"}
          name={`locationIds`}
          label={<label htmlFor="locationIds" className="input-label">
            Locations
            </label>}
          validateTrigger={["onChange", "onBlur"]}
          rules={[
            {
              required: true,
              message: "Location is required!"
            }
          ]}>
          <Select
            mode="multiple"
            variant="borderless"
            fieldNames={{ label: "locationName", value: "locationId" }}
            options={locations?.sort((a, b) => a.locationName.localeCompare(b.locationName))}
            allowClear
            showSearch={false}
            ref={locationRef}
            dropdownRender={locationRender} className="filter-input-transparent"
          />
        </Form.Item>

        <Form.Item
          key={"provider"}
          name={`providerIds`}
          label={<label
            htmlFor="providerIds"
            className="input-label">
            Providers
          </label>}
          validateTrigger={["onChange", "onBlur"]}
          rules={[
            {
              required: true,
              message: "Provider is required!"
            }
          ]}
          >
          <Select
            mode="multiple"
            variant="borderless"
            fieldNames={{ label: "providerName", value: "providerId" }}
            options={providers?.sort((a, b) => a.providerName.localeCompare(b.providerName))}
            allowClear
            showSearch={false}
            ref={providerRef}
            dropdownRender={providerRenderer} className="filter-input-transparent"
          />
        </Form.Item>

        <Form.Item
          key={"roles"}
          name={`roleIds`}
          label={<label htmlFor="roleIds" className="input-label">
            Roles
            </label>}
          rules={[
            {
              required: true,
              message: "Role is required!"
            }
          ]}>
          <Select
            mode="multiple"
            variant="borderless"
            fieldNames={{ label: "description", value: "roleId" }}
            options={roles?.sort((a, b) => a.description.localeCompare(b.description))} className="filter-input-transparent"
          />
        </Form.Item>

        <Form.Item
          key={"landingPage"}
          name={`landingPage`}
          label={<label htmlFor="landingPage" className="input-label">
            Landing Page
            </label>}
          rules={[
            {
              required: true,
              message: "Landing Page is required!"
            }
          ]}>
          <Select
            defaultActiveFirstOption
            variant="borderless"
            options={Object.keys(LandingPage).map(key => ({
              label: LandingPage[key as keyof typeof LandingPage],
              value: key
            }))} className="filter-input-transparent"
          />
        </Form.Item>
        {isSelectedContactProvider() && (
          <Form.Item
            key={"mobileApp"}
            name={`mobileApp`}
            label={<label htmlFor="mobileApp" className="input-label">
              Mobile App Choice
            </label>}
            rules={[
              {
                required: true,
                message: "Mobile App is required!"
              }
            ]}>
            <Select
              defaultActiveFirstOption
              variant="borderless"
              options={Object.keys(MobileApp).map(key => ({
                label: MobileApp[key as keyof typeof MobileApp],
                value: key
              }))} className="filter-input-transparent"
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default AddOrUpdateUser;
